import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import { Check, Search, AlertTriangle, Trash2, ArrowRight, RotateCcw, Loader2, ChevronRight, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

export const NormalizationModal = ({ open, onOpenChange, project, onApplied }) => {
  const [step, setStep] = useState('matches'); // matches | fields | confirm
  const [matches, setMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [fieldsData, setFieldsData] = useState({});
  const [selections, setSelections] = useState({}); // {LIEU: "chosen_value", VILLE: "chosen_value", ...}
  const [customInputs, setCustomInputs] = useState({}); // {LIEU: true, ...} - which fields have custom input active
  const [customValues, setCustomValues] = useState({}); // {LIEU: "my custom value", ...}
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [savedRules, setSavedRules] = useState([]);

  // Load matches list
  const loadMatches = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/normalization/matches?project=${encodeURIComponent(project)}`);
      setMatches(res.data.matches || []);
    } catch (err) {
      toast.error('Erreur chargement des matchs');
    }
    setLoading(false);
  }, [project]);

  // Load saved rules
  const loadRules = useCallback(async () => {
    try {
      const res = await api.get(`/normalization/rules?project=${encodeURIComponent(project)}`);
      setSavedRules(res.data.rules || []);
    } catch (err) {
      console.error(err);
    }
  }, [project]);

  // Load fields data for a match
  const loadMatchFields = useCallback(async (matchValue) => {
    setLoading(true);
    try {
      const res = await api.get(`/normalization/match-fields?project=${encodeURIComponent(project)}&match=${encodeURIComponent(matchValue)}`);
      setFieldsData(res.data.fields || {});
      // Pre-select the most common value for each field
      const preSelections = {};
      for (const [field, data] of Object.entries(res.data.fields || {})) {
        if (data.values?.length > 0) {
          preSelections[field] = data.values[0].value; // most common = first
        }
      }
      setSelections(preSelections);
    } catch (err) {
      toast.error('Erreur chargement des champs');
    }
    setLoading(false);
  }, [project]);

  useEffect(() => {
    if (open) {
      loadMatches();
      loadRules();
      setStep('matches');
      setSelectedMatch(null);
      setFieldsData({});
      setSelections({});
      setCustomInputs({});
      setCustomValues({});
      setSearch('');
    }
  }, [open, loadMatches, loadRules]);

  const handleMatchSelect = (matchValue) => {
    setSelectedMatch(matchValue);
    setCustomInputs({});
    setCustomValues({});
    loadMatchFields(matchValue);
    setStep('fields');
  };

  const toggleCustomInput = (fieldLabel) => {
    setCustomInputs(prev => {
      const isActive = !prev[fieldLabel];
      if (!isActive) {
        // Switching back to selection mode: clear custom value, revert to first value
        setCustomValues(v => ({ ...v, [fieldLabel]: '' }));
        setSelections(s => ({ ...s, [fieldLabel]: fieldsData[fieldLabel]?.values?.[0]?.value || '' }));
      } else {
        // Switching to custom: clear selection
        setSelections(s => ({ ...s, [fieldLabel]: null }));
      }
      return { ...prev, [fieldLabel]: isActive };
    });
  };

  const handleApply = async () => {
    // Build replacements: for each field, old_values = all values except the target, new_value = target
    const replacements = {};
    let hasChanges = false;

    for (const [field, data] of Object.entries(fieldsData)) {
      const isCustom = customInputs[field] && customValues[field]?.trim();
      const selectedValue = isCustom ? customValues[field].trim() : selections[field];
      if (!selectedValue) continue;

      // For custom values: ALL existing values become old_values (replace everything)
      // For selected values: only non-selected values become old_values
      const oldValues = isCustom
        ? data.values.map(v => v.value)
        : data.values.map(v => v.value).filter(v => v !== selectedValue);

      if (oldValues.length > 0) {
        replacements[field] = { old_values: oldValues, new_value: selectedValue };
        hasChanges = true;
      }
    }

    if (!hasChanges) {
      toast.info('Aucune normalisation nécessaire');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/normalization/apply-match', {
        match_value: selectedMatch,
        project,
        replacements
      });
      toast.success(`${res.data.modified_count} tickets normalisés ! (Snapshot créé pour retour arrière)`);
      onApplied?.();
      loadRules();
      // Go back to match list
      setStep('matches');
      loadMatches();
    } catch (err) {
      toast.error('Erreur lors de la normalisation');
    }
    setLoading(false);
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      await api.delete(`/normalization/rules/${ruleId}?project=${encodeURIComponent(project)}`);
      toast.success('Règle supprimée');
      loadRules();
    } catch (err) {
      toast.error('Erreur suppression');
    }
  };

  const filteredMatches = matches.filter(m =>
    m.value.toLowerCase().includes(search.toLowerCase())
  );

  // Count fields that need normalization (including custom inputs)
  const fieldsNeedingNorm = Object.entries(fieldsData).filter(([field, d]) => 
    d.needs_normalization || (customInputs[field] && customValues[field]?.trim())
  ).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-[#C4A052]/30 max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-[#C4A052] text-lg">
            Normalisation — {project}
          </DialogTitle>
        </DialogHeader>

        {/* STEP 1: Choose match */}
        {step === 'matches' && (
          <div className="space-y-4">
            <p className="text-zinc-400 text-sm">Choisis le match à normaliser :</p>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <Input
                placeholder="Rechercher un match..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 bg-zinc-900 border-zinc-700 text-white"
                data-testid="norm-search-match"
              />
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-[#C4A052]" />
              </div>
            ) : (
              <div className="max-h-[45vh] overflow-y-auto space-y-1 border border-zinc-800 rounded-lg p-2">
                {filteredMatches.map((m, idx) => (
                  <div
                    key={idx}
                    onClick={() => handleMatchSelect(m.value)}
                    className="flex items-center justify-between px-3 py-2.5 rounded-md cursor-pointer hover:bg-[#C4A052]/10 transition-colors border border-transparent hover:border-[#C4A052]/20"
                    data-testid={`norm-match-${idx}`}
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <ChevronRight className="h-4 w-4 text-[#C4A052] shrink-0" />
                      <span className="text-white text-sm font-medium truncate">{m.value}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-zinc-500 text-xs">{m.count} tickets</span>
                      <span className="text-zinc-600 text-xs">({m.types?.join(' + ')})</span>
                    </div>
                  </div>
                ))}
                {filteredMatches.length === 0 && (
                  <p className="text-zinc-500 text-sm text-center py-4">Aucun match trouvé</p>
                )}
              </div>
            )}

            {/* Saved rules */}
            {savedRules.length > 0 && (
              <div className="mt-4">
                <p className="text-zinc-400 text-sm mb-2">Règles actives ({savedRules.length}) — auto-appliquées sur les imports :</p>
                <div className="space-y-1 max-h-36 overflow-y-auto">
                  {savedRules.map((rule, idx) => (
                    <div key={rule.id || idx} className="flex items-center justify-between bg-zinc-900 rounded px-3 py-1.5 border border-zinc-800">
                      <div className="flex-1 min-w-0 flex items-center gap-1 text-xs">
                        <span className="text-sky-400 font-medium shrink-0">{rule.field}</span>
                        {rule.match_value && <span className="text-zinc-500 truncate">({rule.match_value.slice(0, 20)})</span>}
                        <span className="text-zinc-600">:</span>
                        <span className="text-red-400 truncate">{rule.old_values?.slice(0, 1).join('')}{rule.old_values?.length > 1 ? ` +${rule.old_values.length - 1}` : ''}</span>
                        <ArrowRight className="h-2.5 w-2.5 text-zinc-500 shrink-0" />
                        <span className="text-emerald-400 truncate">{rule.new_value}</span>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteRule(rule.id)} className="h-5 w-5 p-0 text-red-400 hover:text-red-300 shrink-0 ml-1">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP 2: Normalize fields for selected match */}
        {step === 'fields' && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setStep('matches'); setSearch(''); }} className="text-zinc-400">
                <RotateCcw className="h-4 w-4 mr-1" /> Retour
              </Button>
              <span className="text-[#C4A052] font-semibold text-sm truncate">{selectedMatch}</span>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-[#C4A052]" />
              </div>
            ) : (
              <div className="space-y-4">
                {fieldsNeedingNorm === 0 && (
                  <div className="text-center py-6">
                    <Check className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
                    <p className="text-emerald-400 font-medium">Ce match est déjà normalisé !</p>
                    <p className="text-zinc-500 text-sm">Toutes les valeurs sont cohérentes.</p>
                  </div>
                )}

                {Object.entries(fieldsData).map(([fieldLabel, data]) => {
                  const isCustomActive = customInputs[fieldLabel];
                  return (
                  <div key={fieldLabel} className={cn(
                    "rounded-lg border p-3",
                    (data.needs_normalization || isCustomActive) ? "border-amber-500/30 bg-amber-500/5" : "border-zinc-800 bg-zinc-900/50"
                  )}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-white">{fieldLabel}</span>
                        {data.needs_normalization ? (
                          <span className="text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full">
                            {data.values.length} valeurs différentes
                          </span>
                        ) : (
                          <span className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                            {isCustomActive ? 'Personnalisé' : 'OK'}
                          </span>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleCustomInput(fieldLabel)}
                        className={cn("h-7 px-2 text-xs gap-1", isCustomActive ? "text-sky-400" : "text-zinc-500 hover:text-zinc-300")}
                        data-testid={`norm-custom-toggle-${fieldLabel}`}
                      >
                        <Pencil className="h-3 w-3" />
                        {isCustomActive ? 'Annuler' : 'Saisir'}
                      </Button>
                    </div>

                    {/* Custom input mode */}
                    {isCustomActive && (
                      <div className="mb-2">
                        <Input
                          placeholder={`Saisir la valeur pour ${fieldLabel}...`}
                          value={customValues[fieldLabel] || ''}
                          onChange={(e) => setCustomValues(prev => ({ ...prev, [fieldLabel]: e.target.value }))}
                          className="bg-zinc-900 border-sky-500/40 text-white text-sm focus:border-sky-400"
                          data-testid={`norm-custom-input-${fieldLabel}`}
                        />
                        {customValues[fieldLabel]?.trim() && (
                          <p className="text-xs text-sky-400 mt-1">
                            Toutes les valeurs ci-dessous seront remplacées par "{customValues[fieldLabel].trim()}"
                          </p>
                        )}
                      </div>
                    )}

                    {data.values.length > 0 && (
                      <div className="space-y-1">
                        {data.values.map((item, idx) => (
                          <div
                            key={idx}
                            onClick={() => {
                              if (!isCustomActive) {
                                setSelections(prev => ({ ...prev, [fieldLabel]: item.value }));
                              }
                            }}
                            className={cn(
                              "flex items-center justify-between px-3 py-1.5 rounded-md transition-colors",
                              !isCustomActive && "cursor-pointer",
                              isCustomActive
                                ? "opacity-60 border border-transparent"
                                : selections[fieldLabel] === item.value
                                  ? "bg-emerald-500/15 border border-emerald-500/40"
                                  : "hover:bg-zinc-800/50 border border-transparent"
                            )}
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              {!isCustomActive && (
                                <div className={cn(
                                  "w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0",
                                  selections[fieldLabel] === item.value ? "bg-emerald-500 border-emerald-500" : "border-zinc-600"
                                )}>
                                  {selections[fieldLabel] === item.value && <Check className="h-2.5 w-2.5 text-black" />}
                                </div>
                              )}
                              <span className={cn(
                                "text-sm truncate",
                                !isCustomActive && selections[fieldLabel] === item.value ? "text-emerald-400 font-medium" : "text-zinc-300"
                              )}>{item.value}</span>
                            </div>
                            <span className="text-zinc-500 text-xs shrink-0 ml-2">{item.count}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  );
                })}

                {/* Apply button */}
                {fieldsNeedingNorm > 0 && (
                  <div className="bg-zinc-900 rounded-lg p-4 border border-[#C4A052]/20 space-y-3">
                    <div className="flex items-center gap-2 text-amber-400">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="text-sm font-medium">
                        {fieldsNeedingNorm} champ(s) à normaliser
                      </span>
                    </div>
                    <p className="text-xs text-zinc-500">
                      Les valeurs non sélectionnées seront remplacées par les valeurs choisies (vertes ou saisies).
                      Un snapshot sera créé automatiquement (retour arrière possible).
                    </p>
                    <Button
                      onClick={handleApply}
                      disabled={loading}
                      className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
                      data-testid="norm-apply-btn"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                      Normaliser ce match
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NormalizationModal;
